import React from "react";
import { HiX } from "react-icons/hi";
import { FiMenu } from "react-icons/fi";
import { FaHospitalUser } from "react-icons/fa";
import { SidebarLinks } from "./components/Links";
import { useState, useEffect } from "react";
import routes from "routes.js";
import { useNavigate } from "react-router-dom";
import { Tooltip } from "react-tooltip";
import { getSecureLocalItem } from "utils/secureSessionStorage";

const Sidebar = ({ open, onClose }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [hovered, setHovered] = useState(false);
  const navigate = useNavigate();
  // Retrieve from session storage
  const role = getSecureLocalItem("role");

  // Handle responsive behavior
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 1280) {
        setCollapsed(true);
      } else {
        setCollapsed(false);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const isMiniSidebar = collapsed && !hovered;

  // Filter routes to only show head office routes with icons and marked for sidebar
  const visibleRoutes = routes.filter((route) => {
    if (role === "admin") {
      return route.showInSidebar && route.icon && route.layout === "/admin";
    } else if (role === "vendor") {
      return route.showInSidebar && route.icon && route.layout === "/vendor";
    }

    return false;
  });

  // Function to handle icon click
  const handleIconClick = (route) => {
    navigate(`${route.layout}/${route.path}`);
  };

  return (
    <>
      {/* Mini Navigation Bar - Visible when sidebar is closed */}
      {!open && (
        <div className="fixed left-1 z-50 flex  h-screen flex-col items-center gap-3 bg-white pt-6 dark:bg-navy-700 dark:text-gray-300">
          {/* Menu Button */}
          <button
            onClick={() => onClose(true)}
            className=" flex h-12 w-12 items-center justify-center rounded-full bg-indigo-600 p-2 shadow-lg transition-all hover:bg-indigo-700 dark:bg-indigo-500"
            data-tooltip-id="sidebar-tooltip"
            data-tooltip-content="Menu"
            data-tooltip-place="right"
          >
            <FiMenu className="h-6 w-6 text-white" />
          </button>

          {/* Mini Navigation Icons */}
          <div className="table-container1 flex max-h-[85vh] flex-col  items-center gap-3 overflow-y-auto pb-6 pr-2 dark:bg-navy-700 dark:text-gray-300">
            {visibleRoutes.map((route, index) => (
              <button
                key={index}
                className={`flex h-12 w-12 items-center justify-center rounded-full p-2 shadow-lg transition-all
                  ${
                    route.group === "Dashboard"
                      ? "bg-indigo-100 hover:bg-indigo-200 dark:bg-indigo-900/50 dark:hover:bg-indigo-800"
                      : "bg-white hover:bg-gray-100 dark:bg-navy-800 dark:hover:bg-navy-700"
                  }`}
                data-tooltip-id="sidebar-tooltip"
                data-tooltip-content={route.name}
                data-tooltip-place="right"
                onClick={() => handleIconClick(route)}
              >
                {React.cloneElement(route.icon, {
                  className: "h-6 w-6",
                  style: {
                    color:
                      route.group === "Dashboard" ? "#4f46e5" : "currentColor",
                  },
                })}
              </button>
            ))}
          </div>

          {/* Office Info */}
          <div className="flex flex-col items-end gap-1 border-t border-gray-200 pt-2 dark:border-gray-700">
            <div
              className="flex h-12 w-12 items-center justify-center rounded-full bg-indigo-100 p-2 shadow dark:bg-indigo-900/50"
              data-tooltip-id="sidebar-tooltip"
              data-tooltip-content={role}
              data-tooltip-place="right"
            >
              <FaHospitalUser className="h-6 w-6 text-indigo-600 dark:text-indigo-400" />
            </div>
          </div>

          {/* Tooltip */}
          <Tooltip
            id="sidebar-tooltip"
            className="z-50 !bg-gray-800 !px-3 !py-2 !text-sm !text-white !opacity-100"
            noArrow={true}
            delayShow={300}
          />
        </div>
      )}

      {/* Sidebar Container */}
      <div
        className={`fixed left-0 top-0 z-40 flex h-full flex-col bg-white shadow-xl transition-all duration-300 ease-in-out dark:bg-navy-800 ${
          open ? (collapsed ? "w-20 " : "w-64") : "w-0 -translate-x-full"
        }`}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        {/* Sidebar Content */}
        <div className="flex h-full flex-col overflow-hidden">
          {/* Header */}
          <div
            className={`flex items-center p-4 ${
              isMiniSidebar ? "justify-center " : "justify-between"
            }`}
          >
            <div className="flex items-center justify-center ">
              <span className="relative ml-8 text-4xl font-black text-gray-900 dark:text-white">
                SN
                <span className="text-blue-600">O</span>
                RI
                <div className="absolute -bottom-2 left-0 h-1 w-full rounded-full bg-gradient-to-r from-blue-600 to-purple-600"></div>
              </span>
            </div>

            {!isMiniSidebar && (
              <button
                onClick={() => onClose(false)}
                className="rounded-full p-1 text-gray-500 hover:bg-gray-200 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                <HiX className="h-6 w-6 bg-indigo-600 text-white" />
              </button>
            )}
          </div>

          {/* Divider */}
          <div className="h-px bg-gray-200 dark:bg-gray-600" />

          {/* Navigation Links */}
          <div className="table-container1 flex-1 overflow-y-auto overflow-x-hidden py-4">
            <SidebarLinks routes={routes} collapsed={isMiniSidebar} />
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
